import { useState, useEffect, useCallback } from 'react';
import { trackingSteps, sampleOrder } from '../../../mocks/orders';
import DeliveryMap from '../../../components/feature/DeliveryMap';
import { formatEtaRange } from '../../../utils/routeUtils';
import { getAllOrders } from '../../../utils/orderStorage';
import type { AdminOrder, AdminOrderStatus } from '../../../mocks/adminOrders';

const statusIndex: Record<string, number> = {
  placed: 0,
  preparing: 1,
  on_way: 2,
  delivered: 3,
};

const DEMO_DESTINATION = 'Regent Street, Georgetown';

const CUSTOMER_STEPS = [
  { label: 'Order Received', sub: 'Submitted successfully', icon: 'ri-file-list-3-line' },
  { label: 'Payment Confirmed', sub: 'Approved by our team', icon: 'ri-shield-check-line' },
  { label: 'Preparing Order', sub: 'Being packed and prepared', icon: 'ri-store-2-line' },
  { label: 'Out for Delivery', sub: 'Rider on the way', icon: 'ri-motorbike-line' },
  { label: 'Delivered', sub: 'Enjoy your beverages!', icon: 'ri-checkbox-circle-line' },
];

function getActiveStep(status: AdminOrderStatus | 'payment_pending'): number {
  switch (status) {
    case 'payment_pending': return 1;
    case 'pending': return 2;
    case 'processing': return 2;
    case 'on_way': return 3;
    case 'delivered': return 4;
    default: return 1;
  }
}

function getStatusLabel(status: AdminOrderStatus | 'payment_pending'): string {
  switch (status) {
    case 'payment_pending': return 'Awaiting Confirmation';
    case 'pending': return 'Confirmed';
    case 'processing': return 'Preparing';
    case 'on_way': return 'On the Way';
    case 'delivered': return 'Delivered';
    case 'cancelled': return 'Cancelled';
    default: return 'Unknown';
  }
}

export default function TrackingSection() {
  const [currentStatus, setCurrentStatus] = useState<string>(sampleOrder.status);
  const [eta, setEta] = useState(8);
  const [showMap, setShowMap] = useState(false);

  // Real order tracking
  const [orderIdInput, setOrderIdInput] = useState('');
  const [trackedOrder, setTrackedOrder] = useState<AdminOrder | null>(null);
  const [searchError, setSearchError] = useState('');
  const [useRealOrder, setUseRealOrder] = useState(false);

  const activeStep = useRealOrder && trackedOrder
    ? getActiveStep(trackedOrder.status as AdminOrderStatus | 'payment_pending')
    : null;

  const handleSearch = () => {
    const q = orderIdInput.trim().toUpperCase();
    if (!q) { setSearchError('Please enter your Order ID'); return; }
    const found = getAllOrders().find((o) => o.id.toUpperCase() === q);
    if (found) {
      setTrackedOrder(found);
      setUseRealOrder(true);
      setSearchError('');
    } else {
      setTrackedOrder(null);
      setUseRealOrder(false);
      setSearchError('Order not found. Please check your Order ID and try again.');
    }
  };

  const pollRealOrder = useCallback(() => {
    if (!useRealOrder || !trackedOrder) return;
    const updated = getAllOrders().find((o) => o.id === trackedOrder.id);
    if (updated) setTrackedOrder(updated);
  }, [useRealOrder, trackedOrder]);

  useEffect(() => {
    const interval = setInterval(pollRealOrder, 3000);
    return () => clearInterval(interval);
  }, [pollRealOrder]);

  useEffect(() => {
    const t = setInterval(() => {
      setEta((prev) => {
        if (prev <= 1) { clearInterval(t); setCurrentStatus('delivered'); return 0; }
        return prev - 1;
      });
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const demoActiveIndex = statusIndex[currentStatus] ?? 2;
  const etaRange = formatEtaRange(DEMO_DESTINATION);

  return (
    <section id="tracking" className="relative py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0cd87958dcfe22bfcef9c12f38bd67f5.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Left — Text */}
          <div>
            <span className="inline-block bg-[#FF6B35]/20 border border-[#FF6B35]/40 text-[#FF6B35] text-xs font-bold px-3 py-1 rounded-full mb-6 tracking-widest uppercase">
              Real-time Tracking
            </span>
            <h2 className="font-black text-white mb-6 leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(48px, 6vw, 80px)', letterSpacing: '0.05em' }}>
              TRACK YOUR<br />
              <span className="text-[#FF6B35]">ORDER LIVE</span>
            </h2>
            <p className="text-white/70 text-base leading-relaxed mb-8 max-w-md">
              Know exactly where your beverages are at every step. From the moment you place your order to your doorstep in Georgetown — full visibility, zero stress.
            </p>

            {/* Order ID Search */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 mb-6">
              <p className="text-white text-sm font-bold mb-3 flex items-center gap-2">
                <i className="ri-search-line text-[#FF6B35]" />
                Track your order by ID
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={orderIdInput}
                  onChange={(e) => { setOrderIdInput(e.target.value); setSearchError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="e.g. SDG-12345"
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 outline-none focus:border-[#FF6B35] focus:bg-white/15 transition"
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-2.5 bg-[#FF6B35] hover:bg-[#e85d2a] text-white text-sm font-bold rounded-xl transition cursor-pointer whitespace-nowrap"
                >
                  Track
                </button>
              </div>
              {searchError && (
                <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-error-warning-line" /> {searchError}
                </p>
              )}
              {useRealOrder && trackedOrder && (
                <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line" /> Showing live status for <strong className="ml-0.5">{trackedOrder.id}</strong>
                </p>
              )}
            </div>

            <div className="space-y-4">
              {[
                { icon: 'ri-map-2-line', title: 'Google Maps Route', desc: 'Real driving route calculated live for every order' },
                { icon: 'ri-notification-3-line', title: 'Instant Notifications', desc: 'SMS & push alerts at every order milestone' },
                { icon: 'ri-timer-2-line', title: 'Accurate ETA', desc: `Google Maps-powered delivery time — typically ${etaRange}` },
              ].map((feat) => (
                <div key={feat.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/20 rounded-xl text-[#FF6B35] shrink-0">
                    <i className={`${feat.icon} text-xl`} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{feat.title}</p>
                    <p className="text-white/50 text-xs">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Tracker Card */}
          <div className="space-y-4">
            <div className="flex items-center gap-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full p-1 w-fit">
              <button
                onClick={() => setShowMap(false)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition cursor-pointer whitespace-nowrap ${!showMap ? 'bg-[#FF6B35] text-white' : 'text-white/60 hover:text-white'}`}
              >
                <i className="ri-list-check-2 mr-1" />Status
              </button>
              <button
                onClick={() => setShowMap(true)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition cursor-pointer whitespace-nowrap ${showMap ? 'bg-[#FF6B35] text-white' : 'text-white/60 hover:text-white'}`}
              >
                <i className="ri-map-2-line mr-1" />Route Map
              </button>
            </div>

            {!showMap ? (
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 text-white">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-white/50 text-xs mb-1">
                      {useRealOrder && trackedOrder ? `Order #${trackedOrder.id}` : `Order #${sampleOrder.id}`}
                    </p>
                    <h4 className="font-bold text-lg">
                      {useRealOrder && trackedOrder ? 'Live Order Status' : 'Demo Tracking'}
                    </h4>
                  </div>
                  {useRealOrder && trackedOrder ? (
                    <div className={`rounded-2xl px-4 py-2 text-center ${
                      trackedOrder.status === 'delivered' ? 'bg-green-500' :
                      trackedOrder.status === 'cancelled' ? 'bg-red-500' :
                      trackedOrder.status === 'payment_pending' ? 'bg-amber-500' : 'bg-[#FF6B35]'
                    }`}>
                      <p className="text-xs text-white/80">Status</p>
                      <p className="font-black text-sm">{getStatusLabel(trackedOrder.status as AdminOrderStatus | 'payment_pending')}</p>
                    </div>
                  ) : currentStatus !== 'delivered' && eta > 0 ? (
                    <div className="bg-[#FF6B35] rounded-2xl px-4 py-2 text-center">
                      <p className="text-xs text-white/80">ETA</p>
                      <p className="font-black text-xl">{eta} min</p>
                    </div>
                  ) : (
                    <div className="bg-green-500 rounded-2xl px-4 py-2 text-center">
                      <p className="text-xs text-white/80">Status</p>
                      <p className="font-black text-sm">Delivered!</p>
                    </div>
                  )}
                </div>

                {/* Real order steps */}
                {useRealOrder && trackedOrder && trackedOrder.status !== 'cancelled' && (
                  <div className="space-y-4 mb-6">
                    {CUSTOMER_STEPS.map((step, i) => {
                      const isDone = i < (activeStep ?? 0);
                      const isActive = i === (activeStep ?? 0);
                      return (
                        <div key={step.label} className="flex items-center gap-4">
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all ${
                              isDone ? 'bg-[#FF6B35] border-[#FF6B35]' :
                              isActive ? 'bg-white/20 border-white' : 'bg-transparent border-white/20'
                            } ${isActive ? 'scale-110' : ''}`}>
                              {isDone
                                ? <i className="ri-check-line text-sm text-white" />
                                : <i className={`${step.icon} text-sm ${isDone ? 'text-white' : isActive ? 'text-white animate-pulse' : 'text-white/30'}`} />
                              }
                            </div>
                            {i < CUSTOMER_STEPS.length - 1 && (
                              <div className={`w-0.5 h-6 mt-1 ${i < (activeStep ?? 0) ? 'bg-[#FF6B35]' : 'bg-white/20'}`} />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-bold ${isDone || isActive ? 'text-white' : 'text-white/30'}`}>
                              {step.label}
                              {i === 3 && trackedOrder.rider && isActive && (
                                <span className="ml-1.5 text-xs text-[#FF6B35] font-normal">· {trackedOrder.rider}</span>
                              )}
                            </p>
                            <p className={`text-xs ${isActive ? 'text-[#FF6B35]' : isDone ? 'text-white/60' : 'text-white/20'}`}>
                              {isActive ? 'In progress...' : step.sub}
                            </p>
                          </div>
                          {isActive && <span className="w-2 h-2 bg-[#FF6B35] rounded-full animate-pulse" />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {useRealOrder && trackedOrder && trackedOrder.status === 'cancelled' && (
                  <div className="flex items-center gap-3 bg-red-500/20 border border-red-400/30 rounded-2xl p-4 mb-6">
                    <i className="ri-close-circle-line text-red-400 text-2xl" />
                    <div>
                      <p className="text-white font-bold text-sm">Order Cancelled</p>
                      <p className="text-white/60 text-xs mt-0.5">This order was cancelled. Contact support for assistance.</p>
                    </div>
                  </div>
                )}

                {/* Demo steps (shown when no real order) */}
                {!useRealOrder && (
                  <div className="space-y-4 mb-6">
                    {trackingSteps.map((step, i) => {
                      const isDone = i <= demoActiveIndex;
                      const isActive = i === demoActiveIndex;
                      return (
                        <div key={step.id} className="flex items-center gap-4">
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all ${
                              isDone ? 'bg-[#FF6B35] border-[#FF6B35]' : 'bg-transparent border-white/20'
                            } ${isActive ? 'scale-110' : ''}`}>
                              <i className={`${step.icon} text-sm ${isDone ? 'text-white' : 'text-white/30'}`} />
                            </div>
                            {i < trackingSteps.length - 1 && (
                              <div className={`w-0.5 h-6 mt-1 ${i < demoActiveIndex ? 'bg-[#FF6B35]' : 'bg-white/20'}`} />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-bold ${isDone ? 'text-white' : 'text-white/30'}`}>{step.label}</p>
                            <p className={`text-xs ${isDone ? 'text-white/60' : 'text-white/20'}`}>{step.description}</p>
                          </div>
                          {step.time && isDone && <span className="text-xs text-[#FF6B35] font-semibold">{step.time}</span>}
                          {isActive && currentStatus !== 'delivered' && <span className="w-2 h-2 bg-[#FF6B35] rounded-full animate-pulse" />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Rider card — demo */}
                {!useRealOrder && (
                  <div className="bg-white/10 rounded-2xl p-4 flex items-center gap-3">
                    <img src={sampleOrder.rider.avatar} alt={sampleOrder.rider.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <p className="font-bold text-sm">{sampleOrder.rider.name}</p>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 flex items-center justify-center text-amber-400">
                          <i className="ri-star-fill text-xs" />
                        </div>
                        <span className="text-xs text-white/70">{sampleOrder.rider.rating} · Demo Rider</span>
                      </div>
                    </div>
                    <div className="ml-auto flex gap-2">
                      <button className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 cursor-pointer transition-colors">
                        <i className="ri-phone-line text-sm" />
                      </button>
                      <button className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 cursor-pointer transition-colors">
                        <i className="ri-message-2-line text-sm" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Rider card — real order */}
                {useRealOrder && trackedOrder && trackedOrder.rider && trackedOrder.status !== 'cancelled' && (
                  <div className="bg-white/10 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#FF6B35] flex items-center justify-center text-white font-black text-lg flex-shrink-0">
                      {trackedOrder.rider.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-sm">{trackedOrder.rider}</p>
                      <p className="text-xs text-white/60">Your Rider</p>
                    </div>
                    <div className="ml-auto flex gap-2">
                      <button className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 cursor-pointer transition-colors">
                        <i className="ri-phone-line text-sm" />
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setShowMap(true)}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-white/10 hover:bg-white/20 rounded-2xl text-white text-sm font-semibold transition cursor-pointer"
                >
                  <i className="ri-map-2-line" />
                  View Route on Google Maps
                </button>

                {useRealOrder && (
                  <button
                    onClick={() => { setUseRealOrder(false); setTrackedOrder(null); setOrderIdInput(''); }}
                    className="mt-2 w-full flex items-center justify-center gap-2 py-2 text-white/40 hover:text-white/70 text-xs transition cursor-pointer"
                  >
                    <i className="ri-arrow-left-line" /> Back to demo
                  </button>
                )}
              </div>
            ) : (
              <div className="rounded-3xl overflow-hidden border border-white/20">
                <DeliveryMap destination={useRealOrder && trackedOrder ? trackedOrder.address : DEMO_DESTINATION} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
